/**
 * Where the user documentation lives, and the page a refused docs request is sent to (Plans/user-documentation.md).
 *
 * nginx answers a /docs/ request without a good docs cookie by redirecting to `/docs-signin` with the path it
 * wanted appended — `/docs-signin/docs/add-devices/windows/` — so the path never needs escaping.
 */
export const DOCS_ROOT = '/docs/'
export const DOCS_SIGNIN = '/docs-signin'

/** The docs page a /docs-signin URL asks for; the docs' front page for anything that isn't one. */
export function docsTarget(pathname: string, search: string, hash = ''): string {
  const path = pathname.startsWith(DOCS_SIGNIN) ? pathname.slice(DOCS_SIGNIN.length) : ''
  // Only a docs page on this origin: never a way to send someone elsewhere after signing in.
  if (!path.startsWith(DOCS_ROOT) || path.includes('//') || path.includes('\\')) return DOCS_ROOT
  return `${path}${search}${hash}`
}

/**
 * The docs page for each part of the portal, for the header's help button: the longest route prefix that matches
 * wins. Paths are under DOCS_ROOT, with an optional #heading. `docsLinks.test.ts` checks each page and heading
 * exists in src/Relay.Docs, so a renamed page or heading fails the build here rather than leaving a dead link.
 */
export const DOCS_FOR_ROUTE: ReadonlyArray<readonly [route: string, page: string]> = [
  ['/', 'fleet/dashboard/'],
  ['/dashboard', 'fleet/dashboard/'],
  ['/devices', 'fleet/devices/'],
  ['/sites', 'fleet/locations-and-map/#sites'],
  ['/admin/locations', 'fleet/locations-and-map/#managing-sites'],
  ['/locations', 'fleet/locations-and-map/#a-sites-page'],
  ['/map', 'fleet/locations-and-map/#the-map'],
  ['/printers', 'fleet/printers/'],
  ['/groups', 'fleet/groups/'],
  ['/sessions', 'remote-support/sessions/'],
  ['/settings/profiles', 'fleet/settings-profiles/'],
  ['/settings/explorer', 'fleet/settings-profiles/#find-where-a-setting-is-set'],
  ['/settings/firmware', 'fleet/firmware/'],
  ['/settings/windows-updates', 'fleet/windows-updates/'],
  ['/settings/agent-versions', 'fleet/agent-versions/'],
  ['/rollouts', 'fleet/rollouts/'],
  ['/rollout-plans', 'fleet/rollouts/#rollout-plans'],
  ['/insights/compliance', 'fleet/compliance/'],
  ['/alerts', 'fleet/alerts/'],
  ['/alerts/notifications', 'fleet/alerts/#your-notifications'],
  ['/alerts/setup', 'administration/alerting/'],
  ['/activity', 'fleet/activity-and-logs/'],
  ['/system-logs', 'fleet/activity-and-logs/#system-logs'],
  ['/jobs', 'fleet/jobs/'],
  ['/jobs/schedules', 'fleet/jobs/#schedules'],
  ['/jobs/queue', 'fleet/jobs/#schedules'],
  ['/jobs/runs', 'fleet/jobs/#run-a-job'],
  ['/jobs/approvals', 'fleet/jobs/#approvals'],
  ['/admin/brands', 'administration/system/#brands'],
  ['/admin/device-types', 'administration/system/#device-types'],
  ['/admin/settings', 'administration/system/#server-settings'],
  ['/admin/authorization', 'administration/system/#authorization-review'],
  ['/admin/audit', 'administration/system/#authorization-review'],
  ['/admin/users', 'administration/users/'],
  ['/admin/organizations', 'administration/organizations/'],
  ['/admin/location-groups', 'administration/location-groups/'],
  ['/admin/roles', 'administration/roles/'],
  ['/admin/devices', 'add-devices/removing/'],
  ['/provisioning', 'add-devices/'],
  // The one page Phase 5 built, so Help lands on the section that explains it rather than the
  // chapter's front page (Plans/esper-ux-parity.md, Phase 7).
  ['/provisioning/add', 'add-devices/#one-page-for-all-of-it'],
  ['/provisioning/claims', 'add-devices/zero-touch/#claims'],
  ['/provisioning/expected', 'add-devices/zero-touch/#register-devices-before-they-arrive'],
  ['/provisioning/queue', 'add-devices/approving/#the-enrollment-queue'],
  ['/provisioning/activity', 'add-devices/approving/#enrollment-activity'],
  ['/provisioning/pairing', 'add-devices/approving/#claim-a-device-by-pairing-code'],
  ['/provisioning/type-rules', 'add-devices/approving/#device-type-rules'],
  ['/provisioning/api-keys', 'api/guides/factory-provisioning/'],
  ['/downloads/support', 'remote-support/install-support-app/'],
  ['/connect/video', 'remote-support/desktop/'],
  ['/connect/vnc', 'remote-support/desktop/'],
  ['/connect/backstage', 'remote-support/backstage/'],
  ['/connect/ssh', 'remote-support/shell/'],
  ['/connect/files', 'remote-support/files/'],
  ['/connect/processes', 'remote-support/processes-and-containers/'],
  ['/connect/docker', 'remote-support/processes-and-containers/#containers'],
]

/** The docs page about the portal page at `pathname`; the docs' front page when no entry matches. */
export function docsFor(pathname: string): string {
  let best: readonly [string, string] | undefined
  for (const entry of DOCS_FOR_ROUTE) {
    const [route] = entry
    const matches =
      route === '/' ? pathname === '/' : pathname === route || pathname.startsWith(`${route}/`)
    if (matches && (!best || route.length > best[0].length)) best = entry
  }
  return best ? `${DOCS_ROOT}${best[1]}` : DOCS_ROOT
}
